import { useState, type FormEvent } from "react";
import type {
  AiAgentSettingsData,
  AiAgentContextProfile,
  AiAgentInstruction,
  AiAgentPreviewResult,
  AiVoiceLearningData,
  ReviewItem,
} from "../types";
import { StarRating } from "../StarRating";
import { originalReviewText, splitGoogleReviewText } from "../review-text";
import type { PlatformApiClient } from "../../api";

const TONE_OPTIONS = [
  { value: "cercano_profesional", label: "Cercano y profesional" },
  { value: "calido", label: "Cálido" },
  { value: "formal", label: "Formal" },
  { value: "breve_directo", label: "Breve y directo" },
] as const;

const LENGTH_OPTIONS = [
  { value: "corta", label: "Corta (1-2 oraciones)" },
  { value: "normal", label: "Normal (párrafo equilibrado)" },
  { value: "detallada", label: "Detallada (atención puntual a cada punto)" },
] as const;

const NICHE_OPTIONS = [
  { id: "cafe", label: "Cafetería" },
  { id: "bakery", label: "Panadería" },
  { id: "cafe_bakery", label: "Cafetería y panadería" },
  { id: "restaurant", label: "Restaurante" },
  { id: "retail", label: "Comercio / Tienda" },
  { id: "services", label: "Servicios profesionales" },
];

export function AiAgentView({
  settings: initialSettings,
  contextProfile: initialContextProfile,
  instructions: initialInstructions,
  voiceLearning,
  reviews,
  slug,
  api,
  reload,
}: {
  settings: AiAgentSettingsData;
  contextProfile: AiAgentContextProfile;
  instructions: AiAgentInstruction[];
  voiceLearning?: AiVoiceLearningData;
  reviews: ReviewItem[];
  slug: string;
  api: PlatformApiClient;
  reload: () => void;
}) {
  const safeAutoConsentAlreadyActive = initialSettings.mode === "safe_auto"
    && Boolean(initialSettings.safe_auto_consent_active);
  const [mode, setMode] = useState(initialSettings.mode || "approval_required");
  const [activeSection, setActiveSection] = useState<"operation" | "voice" | "lab">("operation");
  const [safeAutoConsentConfirmed, setSafeAutoConsentConfirmed] = useState(safeAutoConsentAlreadyActive);
  const [tone, setTone] = useState(initialSettings.tone || "cercano_profesional");
  const [responseLength, setResponseLength] = useState(initialSettings.response_length || "normal");
  const [useCustomerName, setUseCustomerName] = useState(Boolean(initialSettings.use_customer_name));
  const [thankCustomer, setThankCustomer] = useState(Boolean(initialSettings.thank_customer));
  const [signWithBusinessName, setSignWithBusinessName] = useState(Boolean(initialSettings.sign_with_business_name));
  const [businessContext, setBusinessContext] = useState(initialSettings.business_context || "");
  const [customInstructions, setCustomInstructions] = useState(initialSettings.custom_instructions || "");
  const [nicheType, setNicheType] = useState(initialContextProfile.niche_type || "cafe_bakery");

  const [noEmoji, setNoEmoji] = useState(() => {
    return initialInstructions.some(
      (i) => i.status === "active" && /^no usar emojis\.?$/i.test(i.instruction_text || "")
    );
  });

  const unansweredReviews = reviews.filter(
    (review) => review.public_response_status === "sin_responder" && review.actionable !== false
  );
  const initialRealReview = unansweredReviews[0] || null;
  const [selectedReviewId, setSelectedReviewId] = useState(initialRealReview?.id || "");
  const [voiceFilter, setVoiceFilter] = useState("");
  const [voiceBusyId, setVoiceBusyId] = useState<string | null>(null);
  const [caseConsentIds, setCaseConsentIds] = useState<string[]>([]);

  // Sandbox state
  const [testText, setTestText] = useState(initialRealReview ? (initialRealReview.text || "") : "Excelente café y atención del equipo");
  const [testRating, setTestRating] = useState(String(initialRealReview?.rating || 5));
  const [testAuthor, setTestAuthor] = useState(initialRealReview?.author || "Cliente de prueba");
  const [previewResult, setPreviewResult] = useState<AiAgentPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // General state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  function notifySuccess(msg: string) {
    setSaveSuccess(msg);
    setTimeout(() => setSaveSuccess(null), 4000);
  }

  function chooseMode(nextMode: typeof mode) {
    setMode(nextMode);
    if (nextMode === "safe_auto") {
      setSafeAutoConsentConfirmed(safeAutoConsentAlreadyActive);
    } else {
      setSafeAutoConsentConfirmed(false);
    }
  }

  async function handleSaveSettings(e: FormEvent) {
    e.preventDefault();
    setSaveError(null);
    if (mode === "safe_auto" && !safeAutoConsentConfirmed) {
      setSaveError("Confirmá la autorización de respuestas automáticas antes de activar este modo.");
      return;
    }

    setSaving(true);
    try {
      if (mode === "paused") {
        await api.reputation(slug, "/ai-agent/pause", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: "{}",
        });
        await api.reputation(slug, "/ai-agent/settings", {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            tone,
            response_length: responseLength,
            use_customer_name: useCustomerName,
            thank_customer: thankCustomer,
            sign_with_business_name: signWithBusinessName,
            business_context: businessContext.trim() || null,
            custom_instructions: customInstructions.trim() || null,
          }),
        });
      } else {
        await api.reputation(slug, "/ai-agent/settings", {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            mode,
            safe_auto_consent: mode === "safe_auto" ? safeAutoConsentConfirmed : undefined,
            tone,
            response_length: responseLength,
            use_customer_name: useCustomerName,
            thank_customer: thankCustomer,
            sign_with_business_name: signWithBusinessName,
            business_context: businessContext.trim() || null,
            custom_instructions: customInstructions.trim() || null,
          }),
        });
      }

      await api.reputation(slug, "/ai-agent/context-profile", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ niche_type: nicheType }),
      });

      // Handle emoji instruction
      const existingEmojiInst = initialInstructions.find((i) =>
        /^no usar emojis\.?$/i.test(i.instruction_text || "")
      );
      if (noEmoji && !existingEmojiInst) {
        await api.reputation(slug, "/ai-agent/instructions", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ instruction_text: "No usar emojis." }),
        });
      } else if (!noEmoji && existingEmojiInst) {
        await api.reputation(slug, `/ai-agent/instructions/${encodeURIComponent(existingEmojiInst.id)}`, {
          method: "DELETE",
        });
      }

      notifySuccess("Configuración del Agente IA guardada correctamente.");
      reload();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "No se pudo guardar la configuración.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleVoiceReference(exampleId: string, preferred: boolean) {
    setVoiceBusyId(exampleId);
    setSaveError(null);
    try {
      await api.reputation(slug, `/ai-agent/voice-learning/${encodeURIComponent(exampleId)}/preference`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ preferred }),
      });
      notifySuccess(preferred
        ? "Respuesta marcada como referencia preferida de voz."
        : "La respuesta volvió al historial general de aprendizaje.");
      reload();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "No se pudo actualizar la referencia de voz.");
    } finally {
      setVoiceBusyId(null);
    }
  }

  async function toggleRoutineCaseAuthorization(exampleId: string, authorized: boolean) {
    setVoiceBusyId(exampleId);
    setSaveError(null);
    try {
      await api.reputation(slug, `/ai-agent/voice-learning/${encodeURIComponent(exampleId)}/case-authorization`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ authorized, confirmed: authorized && caseConsentIds.includes(exampleId) }),
      });
      setCaseConsentIds((current) => current.filter((id) => id !== exampleId));
      notifySuccess(authorized
        ? "Caso aprobado para respuestas futuras similares, siempre sujeto a los controles de riesgo."
        : "Autorización revocada. Los casos similares volverán a revisión humana.");
      reload();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "No se pudo actualizar la autorización del caso.");
    } finally {
      setVoiceBusyId(null);
    }
  }

  async function runSandboxTest() {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewResult(null);
    try {
      const res = await api.reputation<AiAgentPreviewResult>(slug, "/ai-agent/preview", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(selectedReviewId
          ? { review_id: selectedReviewId }
          : {
              review_text: testText,
              text: testText,
              rating: Number(testRating),
              author: testAuthor,
              author_name: testAuthor,
            }),
      });
      setPreviewResult(res);
    } catch (cause) {
      setPreviewError(
        cause instanceof Error ? cause.message : "Error al generar la prueba de respuesta."
      );
    } finally {
      setPreviewLoading(false);
    }
  }

  const maxChars = initialSettings.limits?.business_context_max || 800;
  const remainingChars = maxChars - businessContext.length;
  const customInstructionsMax = initialSettings.limits?.custom_instructions_max || 800;
  const customInstructionsRemaining = customInstructionsMax - customInstructions.length;
  const normalizedVoiceFilter = voiceFilter.trim().toLocaleLowerCase();
  const visibleVoiceExamples = (voiceLearning?.examples || [])
    .filter((example) => {
      if (!normalizedVoiceFilter) return true;
      return [example.author, example.review_excerpt, example.reply]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(normalizedVoiceFilter));
    })
    .slice(0, 20);
  const hasUnsavedSettings = mode !== (initialSettings.mode || "approval_required")
    || (mode === "safe_auto" && safeAutoConsentConfirmed !== safeAutoConsentAlreadyActive)
    || tone !== (initialSettings.tone || "cercano_profesional")
    || responseLength !== (initialSettings.response_length || "normal")
    || useCustomerName !== Boolean(initialSettings.use_customer_name)
    || thankCustomer !== Boolean(initialSettings.thank_customer)
    || signWithBusinessName !== Boolean(initialSettings.sign_with_business_name)
    || businessContext !== (initialSettings.business_context || "")
    || customInstructions !== (initialSettings.custom_instructions || "")
    || nicheType !== (initialContextProfile.niche_type || "cafe_bakery")
    || noEmoji !== initialInstructions.some((i) => i.status === "active" && /^no usar emojis\.?$/i.test(i.instruction_text || ""));
  const persistedModeLabel = initialSettings.mode === "safe_auto" ? "Automático seguro"
    : initialSettings.mode === "paused" ? "Pausado" : "Aprobación previa";
  const modeSummary = mode === "paused"
    ? {
      label: "Pausado",
      tone: "paused",
      headline: "La automatización está detenida",
      detail: "No genera ni publica respuestas de forma automática. El sandbox y las herramientas manuales siguen disponibles para probar y ajustar el agente.",
      generation: "Sólo cuando vos la pedís",
      publication: "Sin publicación automática",
      sensitive: "Siempre bajo revisión humana",
    }
    : mode === "approval_required"
      ? {
        label: "Con aprobación",
        tone: "approval",
        headline: "La IA prepara; una persona decide",
        detail: "El agente puede redactar borradores, pero ninguna respuesta sale a Google sin una confirmación humana.",
        generation: "Borradores asistidos por IA",
        publication: "Requiere aprobación humana",
        sensitive: "Se mantiene bajo revisión",
      }
      : {
        label: "Automático seguro",
        tone: "automatic",
        headline: safeAutoConsentConfirmed
          ? "Automatización con guardrails"
          : "Falta confirmar la autorización",
        detail: safeAutoConsentConfirmed
          ? "Sólo los casos que pasan los controles configurados podrían avanzar tras 10 minutos como mínimo, si el servicio habilitó explícitamente la publicación automática. Los casos sensibles se derivan."
          : "El modo puede configurarse, pero no se activa hasta que confirmes explícitamente la autorización de respuestas automáticas.",
        generation: "Evaluación y borrador automáticos",
        publication: safeAutoConsentConfirmed ? "Sólo si pasa los guardrails" : "Pendiente de autorización",
        sensitive: "Se deriva a revisión humana",
      };

  return (
    <div className="rep-ai-agent-view rep-layout-approved" data-layout="RB-AGT-B">
      <header className="page-heading">
        <p className="eyebrow">Agente IA</p>
        <h1>Configuración del Agente</h1>
        <p>Definí cómo responde el agente a tus clientes, respetando tus reglas de negocio y tono.</p>
      </header>

      {saveSuccess && <div className="success-alert" role="status">{saveSuccess}</div>}
      {saveError && <div className="inline-alert" role="alert">{saveError}</div>}

      <div className="rep-agent-workspace">
        <div className="rep-agent-task-nav" role="group" aria-label="Secciones del Agente IA">
        <button type="button" aria-controls="rep-agent-operation" aria-pressed={activeSection === "operation"}
          className={activeSection === "operation" ? "active" : ""} onClick={() => setActiveSection("operation")}>
          Funcionamiento
        </button>
        <button type="button" aria-controls="rep-agent-voice" aria-pressed={activeSection === "voice"}
          className={activeSection === "voice" ? "active" : ""} onClick={() => setActiveSection("voice")}>
          Personalidad y memoria
        </button>
        <button type="button" aria-controls="rep-agent-lab" aria-pressed={activeSection === "lab"}
          className={activeSection === "lab" ? "active" : ""} onClick={() => setActiveSection("lab")}>
          Laboratorio
        </button>
      </div>

        <div className="rep-agent-work-panel">
      {hasUnsavedSettings && (
        <div className="rep-agent-unsaved-notice" role="status">
          <strong>Hay ajustes sin guardar.</strong>
          {activeSection === "lab"
            ? " La prueba usa la última configuración guardada, no estos cambios."
            : ` El modo guardado sigue siendo «${persistedModeLabel}» hasta guardar los cambios.`}
        </div>
      )}

      <section id="rep-agent-operation" hidden={activeSection !== "operation"} className={`rep-agent-status-hero ${modeSummary.tone}`} aria-label="Modo seleccionado del Agente IA">
        <div className="rep-agent-status-main">
          <span className="rep-command-kicker">{hasUnsavedSettings ? "Modo seleccionado · pendiente de guardar" : "Configuración guardada"}</span>
          <div className="rep-agent-status-title">
            <span className="rep-agent-status-icon" aria-hidden="true" />
            <div>
              <strong>{modeSummary.label}</strong>
              <h2>{modeSummary.headline}</h2>
            </div>
          </div>
          <p>{modeSummary.detail}</p>
        </div>
        <div className="rep-agent-truth-grid" aria-label="Qué hace el agente en este modo">
          <div>
            <span>Generación</span>
            <strong>{modeSummary.generation}</strong>
          </div>
          <div>
            <span>Publicación</span>
            <strong>{modeSummary.publication}</strong>
          </div>
          <div>
            <span>Casos sensibles</span>
            <strong>{modeSummary.sensitive}</strong>
          </div>
        </div>
      </section>

      <form onSubmit={handleSaveSettings} className="rep-ai-config-form" hidden={activeSection === "lab"}>
        {/* Automation Modes */}
        <section className="rep-agent-section" hidden={activeSection !== "operation"}>
          <div className="rep-section-header">
            <div>
              <p className="eyebrow">Modo de operación</p>
              <h2>¿Cómo querés que responda el agente?</h2>
            </div>
          </div>

          <div className="rep-modes-grid" role="radiogroup" aria-label="Modo de automatización">
            <label className={`rep-mode-card ${mode === "safe_auto" ? "selected" : ""}`}>
              <div className="rep-mode-card-header">
                <input
                  type="radio"
                  name="agent-mode"
                  value="safe_auto"
                  checked={mode === "safe_auto"}
                  onChange={() => chooseMode("safe_auto")}
                />
                <strong>Automático seguro</strong>
                <span className="rep-mode-badge green">Con guardrails</span>
              </div>
              <p>
                Puede publicar únicamente si la publicación automática está habilitada por el servicio y existe autorización vigente.
                Espera al menos 10 minutos desde que llega la reseña y sus últimas actualizaciones antes de responder.
                Las reseñas de 1–2 estrellas necesitan un caso similar autorizado expresamente; los casos ambiguos o sensibles siempre se derivan.
              </p>
            </label>

            <label className={`rep-mode-card ${mode === "approval_required" ? "selected" : ""}`}>
              <div className="rep-mode-card-header">
                <input
                  type="radio"
                  name="agent-mode"
                  value="approval_required"
                  checked={mode === "approval_required"}
                  onChange={() => chooseMode("approval_required")}
                />
                <strong>Aprobación previa</strong>
              </div>
              <p>
                Prepara un borrador para cada reseña y espera tu confirmación antes de publicar.
              </p>
            </label>

            <label className={`rep-mode-card ${mode === "paused" ? "selected" : ""}`}>
              <div className="rep-mode-card-header">
                <input
                  type="radio"
                  name="agent-mode"
                  value="paused"
                  checked={mode === "paused"}
                  onChange={() => chooseMode("paused")}
                />
                <strong>Pausado</strong>
                <span className="rep-mode-badge gray">Inactivo</span>
              </div>
              <p>
                No genera ni publica respuestas de forma automática. Podés seguir usando el sandbox y las herramientas manuales, y reactivarlo cuando quieras.
              </p>
            </label>
          </div>

          {mode === "safe_auto" && (
            <div className="settings-note" style={{ marginTop: "16px" }}>
              <label className="rep-toggle-item" htmlFor="safe-auto-consent">
                <input
                  id="safe-auto-consent"
                  type="checkbox"
                  checked={safeAutoConsentConfirmed}
                  onChange={(e) => setSafeAutoConsentConfirmed(e.target.checked)}
                />
                <span>
                  <strong>Autorización de respuestas automáticas.</strong>{" "}
                  Autorizo a ClickDialog a publicar respuestas en nombre del negocio cuando el
                  sistema determine que pasan los controles configurados. Entiendo que las estrellas
                  no deciden por sí solas la publicación y que puedo pausar esta autorización en
                  cualquier momento.
                </span>
              </label>
              {safeAutoConsentAlreadyActive && initialSettings.safe_auto_consent_granted_at && (
                <p className="rep-hint-text">
                  Hay una autorización vigente registrada desde {initialSettings.safe_auto_consent_granted_at}.
                </p>
              )}
            </div>
          )}
        </section>

        {/* Style & Personality */}
        <div id="rep-agent-voice" className="rep-agent-voice-workspace" hidden={activeSection !== "voice"}>
        <section className="rep-agent-section">
          <div className="rep-section-header">
            <div>
              <p className="eyebrow">Personalidad y redacción</p>
              <h2>Tono y estilo de respuesta</h2>
            </div>
          </div>

          <div className="rep-field-grid">
            <div className="field">
              <label htmlFor="agent-tone">Tono de voz</label>
              <select
                id="agent-tone"
                value={tone}
                onChange={(e) => setTone(e.target.value as typeof tone)}
              >
                {TONE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="agent-length">Longitud de respuesta</label>
              <select
                id="agent-length"
                value={responseLength}
                onChange={(e) => setResponseLength(e.target.value as typeof responseLength)}
              >
                {LENGTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="rep-toggles-grid">
            <label className="rep-toggle-item">
              <input
                type="checkbox"
                checked={useCustomerName}
                onChange={(e) => setUseCustomerName(e.target.checked)}
              />
              <span>Usar el nombre del cliente cuando esté disponible</span>
            </label>

            <label className="rep-toggle-item">
              <input
                type="checkbox"
                checked={thankCustomer}
                onChange={(e) => setThankCustomer(e.target.checked)}
              />
              <span>Agradecer la visita o compra en la apertura</span>
            </label>

            <label className="rep-toggle-item">
              <input
                type="checkbox"
                checked={signWithBusinessName}
                onChange={(e) => setSignWithBusinessName(e.target.checked)}
              />
              <span>Firmar la respuesta con el nombre del negocio</span>
            </label>

            <label className="rep-toggle-item">
              <input
                type="checkbox"
                checked={noEmoji}
                onChange={(e) => setNoEmoji(e.target.checked)}
              />
              <span>Evitar el uso de emojis en las respuestas</span>
            </label>
          </div>
        </section>

        {/* Voice & Learning */}
        <section className="rep-agent-section" aria-label="Voz y aprendizaje">
          <div className="rep-section-header">
            <div>
              <p className="eyebrow">Voz y aprendizaje</p>
              <h2>Enseñale al agente cómo responde tu negocio</h2>
            </div>
          </div>

          <div className="field">
            <label htmlFor="agent-custom-instructions">
              Instrucciones de tu voz ({customInstructionsRemaining} caracteres restantes)
            </label>
            <textarea
              id="agent-custom-instructions"
              rows={5}
              maxLength={customInstructionsMax}
              placeholder="Ej: Español argentino natural. Respuestas breves y cálidas. Reflejar uno o dos detalles concretos de la reseña. Usar emojis con moderación y variar aperturas y cierres."
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
            />
            <p className="rep-hint-text">
              Estas reglas se aplican junto con las respuestas que el agente aprende de tu historial.
            </p>
          </div>

          {voiceLearning ? (
            <>
              <div
                className="rep-agent-truth-grid"
                aria-label="Estado de aprendizaje de voz"
                style={{ marginTop: "18px" }}
              >
                <div>
                  <span>Respuestas aprendidas</span>
                  <strong>{voiceLearning.summary.learned}</strong>
                </div>
                <div>
                  <span>Referencias preferidas</span>
                  <strong>{voiceLearning.summary.preferred}</strong>
                </div>
                <div>
                  <span>Listas para usar</span>
                  <strong>{voiceLearning.summary.ready}</strong>
                </div>
              </div>

              {voiceLearning.examples.length > 0 ? (
                <div style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label htmlFor="voice-learning-filter">Buscar en respuestas aprendidas</label>
                    <input
                      id="voice-learning-filter"
                      type="search"
                      placeholder="Buscar por cliente, reseña o respuesta…"
                      value={voiceFilter}
                      onChange={(e) => setVoiceFilter(e.target.value)}
                    />
                  </div>

                  <div
                    role="region"
                    aria-label="Respuestas aprendidas"
                    tabIndex={0}
                    style={{
                      display: "grid",
                      gap: "12px",
                      marginTop: "14px",
                      maxHeight: "min(74vh, 720px)",
                      overflowY: "auto",
                      paddingRight: "8px",
                    }}
                  >
                    {visibleVoiceExamples.map((example) => (
                      <article
                        key={example.id}
                        className="settings-note"
                        style={{ display: "grid", gap: "10px" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "12px",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                          }}
                        >
                          <div>
                            <strong>{example.author || "Cliente"}</strong>
                            <div style={{ marginTop: "4px" }}>
                              <StarRating rating={example.rating} size="sm" />
                            </div>
                          </div>
                          <span className={example.preferred ? "rep-mode-badge green" : "rep-mode-badge gray"}>
                            {example.preferred ? "Referencia preferida" : "Historial aprendido"}
                          </span>
                        </div>

                        {example.review_excerpt && (
                          <div>
                            <span className="rep-command-kicker">Reseña</span>
                            <p style={{ margin: "4px 0 0" }}>{originalReviewText(example.review_excerpt)}</p>
                            {splitGoogleReviewText(example.review_excerpt).googleTranslation && (
                              <div style={{ marginTop: "10px" }}>
                                <span className="rep-command-kicker">Traducción de Google</span>
                                <p style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>
                                  {splitGoogleReviewText(example.review_excerpt).googleTranslation}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        <div>
                          <span className="rep-command-kicker">Respuesta del negocio</span>
                          <p style={{ margin: "4px 0 0" }}>{example.reply}</p>
                        </div>

                        <div className="button-row">
                          {example.can_toggle_preferred ? (
                            <button
                              type="button"
                              disabled={voiceBusyId === example.id}
                              onClick={() => void toggleVoiceReference(example.id, !example.preferred)}
                            >
                              {voiceBusyId === example.id
                                ? "Guardando…"
                                : example.preferred
                                  ? "Dejar de priorizar"
                                  : "Usar como referencia"}
                            </button>
                          ) : (
                            <span className="rep-hint-text">
                              Esta respuesta ya tiene prioridad porque fue corregida por una persona.
                            </span>
                          )}
                        </div>
                        {example.case_authorization?.eligible && (
                          <div className="settings-note" style={{ display: "grid", gap: "10px" }}>
                            <strong>Aprendizaje operativo · {example.case_authorization.category_label}</strong>
                            <p className="rep-hint-text">
                              Una respuesta aprendida sirve para redactar, pero no autoriza automáticamente otras publicaciones.
                              Para esta clase de caso cotidiano podés habilitar respuestas futuras del mismo local.
                              Los casos delicados, ambiguos o fuera de la política siguen requiriendo revisión.
                            </p>
                            {example.case_authorization.authorized ? (
                              <div className="button-row">
                                <span className="rep-mode-badge green">Casos similares autorizados</span>
                                <button type="button" disabled={voiceBusyId === example.id}
                                  onClick={() => void toggleRoutineCaseAuthorization(example.id, false)}>
                                  Revocar autorización
                                </button>
                              </div>
                            ) : (
                              <>
                                <label className="rep-toggle-item">
                                  <input type="checkbox"
                                    checked={caseConsentIds.includes(example.id)}
                                    onChange={(e) => setCaseConsentIds((current) => e.target.checked
                                      ? [...current.filter((id) => id !== example.id), example.id]
                                      : current.filter((id) => id !== example.id))}
                                  />
                                  Autorizo respuestas automáticas para situaciones cotidianas similares a este caso,
                                  siempre que pasen los controles y el modo Automático seguro esté activo.
                                </label>
                                <div className="button-row">
                                  <button type="button"
                                    disabled={voiceBusyId === example.id || !caseConsentIds.includes(example.id)}
                                    onClick={() => void toggleRoutineCaseAuthorization(example.id, true)}>
                                    {voiceBusyId === example.id ? "Guardando…" : "Autorizar casos similares"}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </article>
                    ))}
                  </div>

                  {visibleVoiceExamples.length === 0 && (
                    <p className="rep-hint-text" style={{ marginTop: "12px" }}>
                      No encontramos respuestas aprendidas con ese filtro.
                    </p>
                  )}
                  {voiceLearning.examples.length > visibleVoiceExamples.length && !normalizedVoiceFilter && (
                    <p className="rep-hint-text" style={{ marginTop: "12px" }}>
                      Mostrando las primeras {visibleVoiceExamples.length} respuestas. Usá el buscador para encontrar una específica.
                    </p>
                  )}
                </div>
              ) : (
                <div className="settings-note" style={{ marginTop: "18px" }}>
                  El agente todavía no tiene respuestas históricas aprendidas. Cuando publiques respuestas,
                  Review Boost podrá incorporarlas como referencias de estilo.
                </div>
              )}
            </>
          ) : (
            <div className="settings-note" style={{ marginTop: "18px" }}>
              La memoria de estilo no está disponible en esta sesión. Podés seguir guardando las instrucciones de voz.
            </div>
          )}
        </section>

        {/* Business Knowledge */}
        <section className="rep-agent-section">
          <div className="rep-section-header">
            <div>
              <p className="eyebrow">Conocimiento del negocio</p>
              <h2>Contexto específico de tu local</h2>
            </div>
          </div>

          <div className="rep-field-grid">
            <div className="field">
              <label htmlFor="agent-niche">Rubro o tipo de negocio</label>
              <select
                id="agent-niche"
                value={nicheType}
                onChange={(e) => setNicheType(e.target.value)}
              >
                {NICHE_OPTIONS.map((n) => (
                  <option key={n.id} value={n.id}>{n.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="field" style={{ marginTop: "16px" }}>
            <label htmlFor="agent-context">
              Información clave sobre tu negocio ({remainingChars} caracteres restantes)
            </label>
            <textarea
              id="agent-context"
              rows={4}
              maxLength={maxChars}
              placeholder="Ej: Café de especialidad con granos de origen. Ofrecemos opciones sin TACC y pastelería artesanal horneada en el día."
              value={businessContext}
              onChange={(e) => setBusinessContext(e.target.value)}
            />
            <p className="rep-hint-text">
              Esta información ayuda al agente a no alucinar y responder con precisión sobre tus productos y servicios.
            </p>
          </div>
        </section>
        </div>

        <div className="rep-agent-settings-actions">
          <button type="submit" className="primary-button" disabled={saving || !hasUnsavedSettings}>
            {saving ? "Guardando cambios…" : hasUnsavedSettings ? "Guardar configuración" : "Configuración guardada"}
          </button>
          <span className="rep-hint-text">Los cambios se aplican al guardar; el laboratorio nunca publica en Google.</span>
        </div>
      </form>

      {/* Sandbox "Probar respuesta" */}
      <section id="rep-agent-lab" hidden={activeSection !== "lab"} className="rep-sandbox-panel" aria-label="Laboratorio de respuestas">
        <div className="rep-section-header">
          <div>
            <p className="eyebrow">Sandbox interactivo</p>
            <h2>Laboratorio de respuestas</h2>
          </div>
          <span className="rep-sandbox-saved-badge">Usa configuración guardada</span>
        </div>
        <p className="rep-section-desc">
          Probá el agente con una reseña real pendiente o con un ejemplo manual. La vista previa genera la respuesta,
          aplica memoria y guardrails, pero <strong>no publica nada en Google</strong>.
        </p>
        <p className="rep-hint-text rep-sandbox-save-hint">
          Esta prueba usa exclusivamente la configuración guardada. Para incorporar ajustes nuevos, entrá en Funcionamiento o Personalidad y memoria, guardalos y volvé al laboratorio.
        </p>

        <div className="rep-sandbox-form">
          <div className="rep-sandbox-inputs">
            {unansweredReviews.length > 0 && (
              <div className="field">
                <label htmlFor="sandbox-real-review">Reseña real pendiente</label>
                <select
                  id="sandbox-real-review"
                  value={selectedReviewId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    setSelectedReviewId(nextId);
                    const review = unansweredReviews.find((item) => item.id === nextId);
                    if (review) {
                      setTestText(review.text || "");
                      setTestRating(String(review.rating));
                      setTestAuthor(review.author || "Cliente");
                    } else {
                      setTestText("Excelente café y atención del equipo");
                      setTestRating("5");
                      setTestAuthor("Cliente de prueba");
                    }
                  }}
                >
                  <option value="">Usar un ejemplo manual</option>
                  {unansweredReviews.map((review) => (
                    <option key={review.id} value={review.id}>
                      {review.author} · {review.rating}★ · {originalReviewText(review.text || "Sin comentario").slice(0, 72)}
                    </option>
                  ))}
                </select>
                <p className="rep-hint-text">
                  Elegir una reseña real usa su <code>review_id</code> y el mismo contexto que usaría Review Boost en producción.
                </p>
              </div>
            )}

            <div className="field">
              <label htmlFor="sandbox-text">
                {selectedReviewId ? "Texto de la reseña real" : "Texto de la reseña de prueba"}
              </label>
              <textarea
                id="sandbox-text"
                rows={3}
                value={selectedReviewId ? originalReviewText(testText) : testText}
                readOnly={Boolean(selectedReviewId)}
                placeholder={selectedReviewId && !testText ? "Esta reseña no incluye comentario de texto." : undefined}
                onChange={(e) => setTestText(e.target.value)}
              />
              {selectedReviewId && splitGoogleReviewText(testText).googleTranslation && (
                <div className="rep-hint-text" style={{ marginTop: "8px" }}>
                  <strong>Traducción de Google</strong>
                  <p style={{ whiteSpace: "pre-wrap", margin: "4px 0 0" }}>
                    {splitGoogleReviewText(testText).googleTranslation}
                  </p>
                </div>
              )}
            </div>

            <div className="rep-sandbox-meta-row">
              <div className="field">
                <label htmlFor="sandbox-rating">Calificación (estrellas)</label>
                <select
                  id="sandbox-rating"
                  value={testRating}
                  disabled={Boolean(selectedReviewId)}
                  onChange={(e) => setTestRating(e.target.value)}
                >
                  <option value="5">5 estrellas ★★★★★</option>
                  <option value="4">4 estrellas ★★★★☆</option>
                  <option value="3">3 estrellas ★★★☆☆</option>
                  <option value="2">2 estrellas ★★☆☆☆</option>
                  <option value="1">1 estrella ★☆☆☆☆</option>
                </select>
              </div>

              <div className="field">
                <label htmlFor="sandbox-author">Autor de prueba</label>
                <input
                  id="sandbox-author"
                  type="text"
                  value={testAuthor}
                  readOnly={Boolean(selectedReviewId)}
                  onChange={(e) => setTestAuthor(e.target.value)}
                />
              </div>

              <div className="rep-sandbox-btn-col">
                <button
                  type="button"
                  className="primary-button"
                  disabled={previewLoading || (!selectedReviewId && !testText.trim())}
                  onClick={runSandboxTest}
                >
                  {previewLoading ? "Evaluando…" : "Probar respuesta"}
                </button>
              </div>
            </div>
          </div>

          {previewError && <div className="inline-alert" role="alert">{previewError}</div>}

          {/* Sandbox Results Display */}
          {previewResult && (
            <div className={`rep-preview-result-card ${previewResult.would_auto_publish ? "safe" : "review"}`}>
              <div className="rep-preview-header">
                <div className={`rep-preview-verdict ${previewResult.would_auto_publish ? "safe" : "review"}`}>
                  <span className="rep-preview-verdict-icon" aria-hidden="true" />
                  <strong>
                    {previewResult.would_auto_publish
                      ? "El caso pasa los controles automáticos"
                      : `Requiere revisión humana (${previewResult.primary_reason || "Requiere atención"})`}
                  </strong>
                </div>
                {previewResult.mode_note && <span className="rep-preview-mode-note">{previewResult.mode_note}</span>}
              </div>

              <div className="rep-preview-body">
                <p>{previewResult.reply?.body || "No se generó respuesta."}</p>
              </div>

              {previewResult.memory && (
                <div className="settings-note" style={{ marginTop: "12px" }}>
                  <strong>
                    {previewResult.memory.memory_applied === true
                      ? `Adaptada con memoria del negocio${(previewResult.memory.memory_examples_used ?? previewResult.personalization?.count) != null ? `: ${previewResult.memory.memory_examples_used ?? previewResult.personalization?.count} referencias utilizadas` : ""}.`
                      : previewResult.memory.memory_applied === false
                        ? "La memoria histórica no se aplicó en esta generación."
                        : "El proveedor no informó si aplicó memoria en esta respuesta."}
                  </strong>
                  {previewResult.response_grounding?.response_source && (
                    <p className="rep-hint-text" style={{ margin: "6px 0 0" }}>
                      Fuente: {previewResult.response_grounding.response_source}
                    </p>
                  )}
                </div>
              )}

              {previewResult.reply?.body && (
                <div className="button-row" style={{ marginTop: "10px" }}>
                  <button
                    type="button"
                    onClick={async () => {
                      await navigator.clipboard.writeText(previewResult.reply?.body || "");
                      notifySuccess("Respuesta de prueba copiada al portapapeles.");
                    }}
                  >
                    Copiar respuesta de prueba
                  </button>
                </div>
              )}

              {previewResult.cafe_bakery_context?.protections && previewResult.cafe_bakery_context.protections.length > 0 && (
                <div className="rep-preview-protections">
                  <h4>Protecciones aplicadas:</h4>
                  <ul>
                    {previewResult.cafe_bakery_context.protections.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(previewResult.ai_run_id || previewResult.provider || previewResult.model || previewResult.memory) && (
                <details className="rep-preview-tech-details">
                  <summary>Ver detalles técnicos de auditoría</summary>
                  <ul>
                    {previewResult.ai_run_id && <li>ID de ejecución: <code>{previewResult.ai_run_id}</code></li>}
                    <li>Proveedor: {previewResult.provider || "No informado"}</li>
                    <li>Modelo: {previewResult.model || "No informado"}</li>
                    <li>Respaldo local: {previewResult.fallback_used == null ? "No informado" : previewResult.fallback_used ? "Sí" : "No"}</li>
                    <li>Memoria aplicada: {previewResult.memory?.memory_applied == null ? "No informado" : previewResult.memory.memory_applied ? "Sí" : "No"}</li>
                    <li>Ejemplos usados: {previewResult.memory?.memory_examples_used ?? previewResult.personalization?.count ?? "No informado"}</li>
                    {previewResult.response_grounding?.response_source && (
                      <li>Fuente de respuesta: {previewResult.response_grounding.response_source}</li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </section>
        </div>
      </div>
    </div>
  );
}
